import { useParams } from '@tanstack/react-router';
import type { UIMessage } from 'ai';
import { BotIcon, SendIcon } from 'lucide-react';
import { memo, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { MarkdownEditor } from '@/components/editor/markdown-editor';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAiChat } from '@/features/pulls/context/ai-chat-context';
import { useSidebar } from '@/features/pulls/context/sidebar-context';
import type { Attachment } from '@/features/pulls/context/sidebar-context';
import { cn } from '@/lib/utils';
import { useUser } from '@/services/user';

const getMessageText = (message: UIMessage): string => {
  return message.parts
    .filter(
      (part): part is { type: 'text'; text: string } =>
        typeof part === 'object' && part !== null && 'type' in part && part.type === 'text' && 'text' in part
    )
    .map((part) => part.text)
    .join('');
};

const formatTimestamp = (message: UIMessage): string => {
  try {
    const metadata = message.metadata as any;
    if (metadata && metadata.timestamp) {
      const date = new Date(metadata.timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  } catch {
    // Ignore any errors parsing timestamp
  }
  return '';
};

const AttachmentCard = memo(({ attachment }: { attachment: Attachment }) => (
  <div className="bg-stone-50 border border-stone-200 rounded-md p-3 mb-3">
    <div className="flex items-center justify-between mb-2">
      <div className="text-xs font-medium text-stone-600">
        {attachment.path}
        {attachment.startLine && (
          <span className="ml-1">
            (lines {attachment.startLine}
            {attachment.endLine && attachment.endLine !== attachment.startLine && `-${attachment.endLine}`})
          </span>
        )}
      </div>
    </div>
    {attachment.code && (
      <pre className="text-xs text-stone-700 bg-stone-100 p-2 rounded overflow-x-auto whitespace-pre-wrap">
        {attachment.code}
      </pre>
    )}
  </div>
));

export function AIChatView() {
  const { owner, repo, number } = useParams({ from: '/_authed/_layout/$owner/$repo/pull/$number' });
  const { user } = useUser();
  const { aiChatInput, aiChatAttachments, dispatch } = useSidebar();
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Use WebSocket context - connection is managed at the page level
  const { messages, sendMessage, isLoading, error, connectionStatus, startTyping, stopTyping } = useAiChat();

  // Handle pre-populated input from context
  useEffect(() => {
    if (aiChatInput) {
      setInputMessage(aiChatInput);
      dispatch({ type: 'SET_AI_CHAT_INPUT', payload: '' }); // Clear after setting
      // Focus the input and scroll to bottom
      setTimeout(() => {
        inputRef.current?.focus();
        if (inputRef.current) {
          inputRef.current.setSelectionRange(inputRef.current.value.length, inputRef.current.value.length);
        }
      }, 100);
    }
  }, [aiChatInput, dispatch]);

  // Handle attachments
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  useEffect(() => {
    if (aiChatAttachments.length > 0) {
      setAttachments(aiChatAttachments);
      dispatch({ type: 'SET_AI_CHAT_ATTACHMENTS', payload: [] }); // Clear after setting
    }
  }, [aiChatAttachments, dispatch]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    // Build contextual message with attachments if present
    let messageContent = inputMessage.trim();
    if (attachments.length > 0) {
      // Add context from attachments to the message
      const contextParts = attachments.map((attachment) => {
        const lineRange =
          attachment.startLine === attachment.endLine
            ? `line ${attachment.startLine}`
            : `lines ${attachment.startLine}-${attachment.endLine}`;
        return `Context from ${attachment.path} (${lineRange}):\n\`\`\`\n${attachment.code}\n\`\`\``;
      });
      messageContent = `${contextParts.join('\n\n')}\n\n${inputMessage.trim()}`;
    }

    try {
      await sendMessage(messageContent);
      // Clear input and context
      setInputMessage('');
      setAttachments([]);
    } catch (err) {
      toast.error('Failed to send message');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Connection status indicator
  const connectionIndicator = () => {
    switch (connectionStatus) {
      case 'connected':
        return <div className="w-2 h-2 bg-green-500 rounded-full" />;
      case 'connecting':
        return <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />;
      case 'error':
        return <div className="w-2 h-2 bg-red-500 rounded-full" />;
      default:
        return <div className="w-2 h-2 bg-gray-400 rounded-full" />;
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Connection status header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b bg-stone-50 text-xs">
        {connectionIndicator()}
        <span className="text-stone-600">Session {connectionStatus}</span>
      </div>

      {/* Chat messages */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
        {messages.length === 0 && attachments.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 px-3 text-center">
            <BotIcon className="size-6 text-blue-600 mb-3" />
            <p className="text-sm text-stone-600 leading-relaxed">
              Ask me anything about this pull request. I can explain changes, suggest improvements, or answer questions.
            </p>
          </div>
        )}

        {/* Show attachments */}
        {attachments.map((attachment, index) => (
          <AttachmentCard key={index} attachment={attachment} />
        ))}

        {messages.map((message: UIMessage) => {
          console.log('🎨 Rendering message:', message);
          console.log('📝 Message text:', getMessageText(message));
          console.log('⚡ isLoading:', isLoading);

          return (
            <div key={message.id} className="space-y-1">
              {/* Message header with role and timestamp */}
              <div
                className={cn(
                  'flex items-center gap-2 text-xs',
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                )}
              >
                {message.role === 'assistant' && (
                  <>
                    <BotIcon className="size-3 text-blue-600" />
                    <span className="text-stone-500 font-medium">AI</span>
                  </>
                )}
                {message.role === 'user' && (
                  <>
                    <span className="text-stone-500 font-medium">You</span>
                    <Avatar className="size-3">
                      <AvatarImage src={user.avatar_url} alt={user.login} />
                      <AvatarFallback className="text-[6px]">{user.login[0]?.toUpperCase()}</AvatarFallback>
                    </Avatar>
                  </>
                )}
                <span className="text-stone-400">
                  {message.role === 'assistant' && !getMessageText(message) && isLoading
                    ? 'typing...'
                    : formatTimestamp(message)}
                </span>
              </div>

              {/* Message content */}
              <div className={cn('flex', message.role === 'user' ? 'justify-end' : 'justify-start')}>
                {message.role === 'user' ? (
                  <div className="inline-block bg-blue-600 text-white text-sm px-3 py-1.5 rounded-lg max-w-[90%] text-left">
                    <p className="whitespace-pre-wrap break-words">{getMessageText(message)}</p>
                  </div>
                ) : (
                  <div className="text-stone-700">
                    {!getMessageText(message) && isLoading ? (
                      // Show typing indicator for empty AI message while loading
                      <div className="text-stone-500">
                        <div className="flex space-x-1">
                          <div className="w-1 h-1 bg-stone-400 rounded-full animate-bounce"></div>
                          <div
                            className="w-1 h-1 bg-stone-400 rounded-full animate-bounce"
                            style={{ animationDelay: '0.1s' }}
                          ></div>
                          <div
                            className="w-1 h-1 bg-stone-400 rounded-full animate-bounce"
                            style={{ animationDelay: '0.2s' }}
                          ></div>
                        </div>
                      </div>
                    ) : (
                      <MarkdownEditor
                        value={getMessageText(message) || ''}
                        editable={false}
                        owner={owner}
                        repo={repo}
                        className="[&_.ProseMirror]:!p-0 [&_.ProseMirror]:text-sm [&_.ProseMirror]:leading-relaxed [&_.ProseMirror_p]:mb-2 [&_.ProseMirror_p:last-child]:mb-0"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <form onSubmit={handleSendMessage} className="border-t bg-white p-3">
        <div className="flex gap-2 items-end">
          <Textarea
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask AI about this pull request..."
            className="flex-1 min-h-[40px] max-h-[120px] resize-none"
            disabled={isLoading || connectionStatus !== 'connected'}
          />
          <Button
            type="submit"
            size="sm"
            disabled={!inputMessage.trim() || isLoading || connectionStatus !== 'connected'}
            className="shrink-0"
          >
            <SendIcon size={14} />
          </Button>
        </div>
      </form>
    </div>
  );
}
